import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Firestore,
} from 'firebase/firestore';
import type {
  ChecklistRepository,
  ChecklistTask,
  CreateChecklistTaskInput,
  Unsubscribe,
} from '@nx/domain';
import { mapChecklistDoc, snapshotData } from './firestoreMappers';

const TEMPLATES_COLLECTION = 'checklistTemplates';

function tasksCol(db: Firestore, eventId: string) {
  return collection(db, 'events', eventId, 'checklistTasks');
}

export class FirebaseChecklistRepository implements ChecklistRepository {
  constructor(private readonly db: Firestore) {}

  async listByEvent(eventId: string): Promise<ChecklistTask[]> {
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        tasksCol(this.db, eventId),
        (snap) => {
          unsub();
          resolve(snap.docs.map((d) => mapChecklistDoc(eventId, d.id, snapshotData(d))));
        },
        reject,
      );
    });
  }

  watchByEvent(eventId: string, listener: (tasks: ChecklistTask[]) => void): Unsubscribe {
    return onSnapshot(tasksCol(this.db, eventId), (snap) => {
      listener(snap.docs.map((d) => mapChecklistDoc(eventId, d.id, snapshotData(d))));
    });
  }

  async create(eventId: string, input: CreateChecklistTaskInput): Promise<ChecklistTask> {
    const ref = await addDoc(tasksCol(this.db, eventId), {
      phase: input.phase,
      title: input.title.trim(),
      notes: input.notes?.trim() ?? null,
      assigneeId: input.assigneeId ?? null,
      dueDate: input.dueDate ?? null,
      priority: input.priority ?? 'medium',
      status: input.status ?? 'todo',
      tags: input.tags ?? [],
      sortOrder: input.sortOrder,
      createdAt: serverTimestamp(),
    });
    const list = await this.listByEvent(eventId);
    const created = list.find((t) => t.id === ref.id);
    if (!created) throw new Error('Task not found after create');
    return created;
  }

  async update(
    eventId: string,
    taskId: string,
    input: Partial<CreateChecklistTaskInput>,
  ): Promise<ChecklistTask> {
    const patch: Record<string, unknown> = {};
    if (input.phase !== undefined) patch.phase = input.phase;
    if (input.title !== undefined) patch.title = input.title.trim();
    if (input.notes !== undefined) patch.notes = input.notes?.trim() ?? null;
    if (input.assigneeId !== undefined) patch.assigneeId = input.assigneeId;
    if (input.dueDate !== undefined) patch.dueDate = input.dueDate;
    if (input.priority !== undefined) patch.priority = input.priority;
    if (input.status !== undefined) patch.status = input.status;
    if (input.tags !== undefined) patch.tags = input.tags;
    if (input.sortOrder !== undefined) patch.sortOrder = input.sortOrder;
    await updateDoc(doc(this.db, 'events', eventId, 'checklistTasks', taskId), patch);
    const list = await this.listByEvent(eventId);
    const updated = list.find((t) => t.id === taskId);
    if (!updated) throw new Error('Task not found after update');
    return updated;
  }

  async delete(eventId: string, taskId: string): Promise<void> {
    await deleteDoc(doc(this.db, 'events', eventId, 'checklistTasks', taskId));
  }

  async applyTemplate(eventId: string, templateId: string): Promise<number> {
    const templateRef = doc(this.db, TEMPLATES_COLLECTION, templateId);
    const templateSnap = await getDoc(templateRef);
    if (!templateSnap.exists()) {
      throw new Error('Template not found');
    }
    const data = snapshotData(templateSnap);
    const tasks = (data.tasks as CreateChecklistTaskInput[]) ?? [];
    const existing = await this.listByEvent(eventId);
    const maxByPhase: Record<string, number> = {};
    for (const t of existing) {
      maxByPhase[t.phase] = Math.max(maxByPhase[t.phase] ?? -1, t.sortOrder);
    }
    const batch = writeBatch(this.db);
    let count = 0;
    for (const task of tasks) {
      const sortOrder = (maxByPhase[task.phase] ?? -1) + 1;
      maxByPhase[task.phase] = sortOrder;
      const ref = doc(tasksCol(this.db, eventId));
      batch.set(ref, {
        phase: task.phase,
        title: task.title,
        notes: task.notes ?? null,
        assigneeId: null,
        dueDate: task.dueDate ?? null,
        priority: task.priority ?? 'medium',
        status: 'todo',
        tags: task.tags ?? [],
        sortOrder,
        createdAt: serverTimestamp(),
      });
      count += 1;
    }
    await batch.commit();
    return count;
  }
}
